import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useState } from "react";
import {
  LayoutDashboard, Map, CalendarRange, UsersRound, Ticket, BarChart3, Send,
  Settings as SettingsIcon, Tag, LogOut, Menu, X, Star, TicketPercent, CalendarDays, Receipt, Users, FileSpreadsheet, Trophy, CreditCard, Gift, Megaphone,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LOGO } from "@/lib/api";
import { GlobalSearch, NotificationBell } from "@/components/admin/TopBar";
import { InstallAppButton } from "@/components/admin/InstallAppButton";
import { PushToggle } from "@/components/admin/PushToggle";
import { OfflineBanner } from "@/lib/offlineLeads";

const STAFF_LINKS = ["Dashboard", "Leads", "Bookings", "Invoices", "Calendar"];
const LINKS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/trips", label: "Trips", icon: Map },
  { to: "/admin/departures", label: "Departures", icon: CalendarRange },
  { to: "/admin/leads", label: "Leads", icon: UsersRound },
  { to: "/admin/bookings", label: "Bookings", icon: Ticket },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/admin/reports", label: "Reports", icon: FileSpreadsheet },
  { to: "/admin/leaderboard", label: "Leaderboard", icon: Trophy },
  { to: "/admin/payments", label: "Payments", icon: CreditCard },
  { to: "/admin/referrals", label: "Referrals", icon: Gift },
  { to: "/admin/customers", label: "Customers", icon: Users },
  { to: "/admin/vouchers", label: "Gift Vouchers", icon: Gift },
  { to: "/admin/messages", label: "WhatsApp Sender", icon: Send },
  { to: "/admin/campaigns", label: "WhatsApp Campaigns", icon: Megaphone },
  { to: "/admin/categories", label: "Categories", icon: Tag },
  { to: "/admin/reviews", label: "Reviews", icon: Star },
  { to: "/admin/coupons", label: "Coupons", icon: TicketPercent },
  { to: "/admin/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/admin/invoices", label: "Invoices", icon: Receipt },
  { to: "/admin/employees", label: "Employees", icon: Users },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon },
];

export default function AdminLayout() {
  const { logout, user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => { await logout(); nav("/admin/login"); };

  const SidebarInner = () => (
    <>
      <Link to="/admin" className="flex items-center gap-2 px-6 h-16 border-b border-border">
        <img src={LOGO} alt="Safarshala" className="h-10 w-auto" />
      </Link>
      <nav className="flex-1 p-3 space-y-1 overflow-auto">
        {LINKS.filter((l) => user?.role === "admin" || STAFF_LINKS.includes(l.label)).map((l) => (
          <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setOpen(false)} data-testid={`admin-nav-${l.label.toLowerCase().replace(/ /g, "-")}`}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:bg-muted"}`}>
            <l.icon className="h-4 w-4" /> {l.label}
          </NavLink>
        ))}
      </nav>
      <PushToggle />
      <InstallAppButton />
      <div className="p-3 border-t border-border">
        <div className="px-3 py-2 text-xs text-muted-foreground truncate">{user?.email}</div>
        <Button variant="ghost" className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10" onClick={doLogout} data-testid="admin-logout">
          <LogOut className="h-4 w-4 mr-2" /> Sign Out
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-muted/30 flex admin-shell">
      <aside className="hidden lg:flex w-64 shrink-0 flex-col bg-card border-r border-border fixed inset-y-0">
        <SidebarInner />
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="relative w-64 flex flex-col bg-card">
            <button className="absolute top-4 right-4" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button>
            <SidebarInner />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="h-14 flex items-center gap-3 px-4 lg:px-8 border-b bg-card sticky top-0 z-40" data-testid="admin-topbar">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(true)} data-testid="admin-menu"><Menu /></Button>
          <GlobalSearch />
          <div className="ml-auto flex items-center gap-2">
            <NotificationBell />
            <span className="hidden sm:block text-xs text-muted-foreground max-w-[160px] truncate">{user?.name}{user?.employee_code ? ` · ${user.employee_code}` : ""}</span>
          </div>
        </header>
        <OfflineBanner />
        <div className="p-4 sm:p-6 lg:p-8 pb-24 lg:pb-8">
          <Outlet />
        </div>
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-card/95 backdrop-blur border-t flex justify-around items-stretch pb-[env(safe-area-inset-bottom)]" data-testid="admin-bottom-nav">
          {[LINKS[0], LINKS[3], LINKS[4], { to: "/admin/invoices", label: "Invoices", icon: Receipt }].map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} className={({ isActive }) => `flex flex-col items-center gap-0.5 py-2 flex-1 text-[11px] font-medium ${isActive ? "text-primary" : "text-muted-foreground"}`} data-testid={`bottom-nav-${l.label.toLowerCase()}`}>
              <l.icon className="h-5 w-5" />{l.label}
            </NavLink>
          ))}
          <button type="button" onClick={() => setOpen(true)} className="flex flex-col items-center gap-0.5 py-2 flex-1 text-[11px] font-medium text-muted-foreground" data-testid="bottom-nav-more"><Menu className="h-5 w-5" />More</button>
        </nav>
      </div>
    </div>
  );
}
